import React, { useContext } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Briefcase,
    CheckCircle2,
    Clock3,
    Star,
    XCircle,
} from "lucide-react";
import { IntlContext } from "context";
import { AdApplicationInterface, AdApplyStatus } from "types/ad";
import { formatRatingAverage } from "helpers/formatRatingAverage";
import Image from "components/File/Image";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { Button } from "components/ui/button";
import { VerifiedProfileBadge } from "components/provider/VerifiedProfileBadge";
import { buildRoute } from "routes";
import { getApplicationConversationUrl, isApplicationBookingCancelled } from "helpers/application/getApplicationConversationUrl";

type ApplicationProvider = {
    id: string;
    picture_id?: string;
    first_name: string;
    last_name: string;
    city: string;
    total_hours: number;
    dataleon_status?: string;
    rating?: { average: number | null; count: number };
};

export type AdApplicationRecord = AdApplicationInterface & {
    provider_id: string;
    provider: ApplicationProvider;
    service: { price: number | string };
    ad: AdApplicationInterface["ad"] & {
        client_id: string;
        booking?: { id: string; validated_at?: string | null; cancelled_at?: string | null } | null;
    };
};

type ApplicationItemProps = {
    application: AdApplicationRecord;
};

const statusConfig: Record<
    AdApplyStatus,
    { labelKey: string; className: string; icon: React.ReactNode }
> = {
    [AdApplyStatus.PENDING]: {
        labelKey: "profile.my_ads.applications.pending",
        className: "bg-amber-50 text-amber-700",
        icon: <Clock3 className="h-3.5 w-3.5" aria-hidden />,
    },
    [AdApplyStatus.HIRED]: {
        labelKey: "profile.my_ads.applications.hired",
        className: "bg-emerald-50 text-emerald-700",
        icon: <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />,
    },
    [AdApplyStatus.ACCEPTED]: {
        labelKey: "profile.my_ads.applications.hired",
        className: "bg-emerald-50 text-emerald-700",
        icon: <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />,
    },
    [AdApplyStatus.REFUSED]: {
        labelKey: "profile.my_ads.applications.refused",
        className: "bg-rose-50 text-rose-700",
        icon: <XCircle className="h-3.5 w-3.5" aria-hidden />,
    },
    [AdApplyStatus.CANCELLED]: {
        labelKey: "profile.my_applications.status.cancelled",
        className: "bg-slate-100 text-slate-600",
        icon: <XCircle className="h-3.5 w-3.5" aria-hidden />,
    },
    [AdApplyStatus.EXPIRED]: {
        labelKey: "profile.my_ads.applications.expired",
        className: "bg-slate-100 text-slate-600",
        icon: <Clock3 className="h-3.5 w-3.5" aria-hidden />,
    },
};

const currencyFormatter = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
});

const acceptedBadgeClassName = "inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700";
const cancelledBadgeClassName = "inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700";

const ApplicationItem: React.FC<ApplicationItemProps> = ({ application }) => {
    const intl = useContext(IntlContext);
    const previousPath = window.location.pathname + window.location.search;
    const provider = application.provider;
    const isPending = application.status === AdApplyStatus.PENDING;
    const isAcceptedOffer =
        application.status === AdApplyStatus.ACCEPTED || application.status === AdApplyStatus.HIRED;
    const isAwaitingPayment = isAcceptedOffer && !application.paid;
    const isPaidAccepted = isAcceptedOffer && Boolean(application.paid);
    const isBookingCancelled = isApplicationBookingCancelled(application);
    const status = statusConfig[application.status];
    const appliedAgo = moment(application.created_at).locale("fr").fromNow();
    const hasRating = Boolean(provider.rating?.average);
    const offerConversationUrl = getApplicationConversationUrl(application);

    return (
        <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
                <span className="text-xs font-medium text-slate-500">
                    {intl.formatMessage("profile.my_ads.applications.applied_ago", { time: appliedAgo })}
                </span>
            </div>
            <div className="space-y-4 p-5">
                <div className="flex items-start gap-4">
                    <Link
                        to={buildRoute.provider(application.service_id)}
                        state={{ previousPath }}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0"
                    >
                        <Avatar shape="tileSm">
                            {provider.picture_id && (
                                <Image
                                    id={provider.picture_id}
                                    className="aspect-square h-full w-full object-cover"
                                />
                            )}
                            <AvatarFallback shape="tileSm" className="text-sm font-semibold">
                                {provider.first_name.charAt(0)}
                                {provider.last_name.charAt(0)}
                            </AvatarFallback>
                        </Avatar>
                    </Link>

                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <Link
                                to={buildRoute.provider(application.service_id)}
                                state={{ previousPath }}
                                target="_blank"
                                rel="noreferrer"
                                className="text-base font-bold text-gray-900 hover:text-primary"
                            >
                                {provider.first_name}
                            </Link>
                            {provider.dataleon_status === "approved" && (
                                <VerifiedProfileBadge size="lg" />
                            )}
                        </div>

                        <p className="mt-1 text-sm text-slate-500">{provider.city}</p>

                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1.5 leading-none">
                                <Briefcase className="h-4 w-4 shrink-0" aria-hidden />
                                {intl.formatMessage("profile.my_ads.applications.hours_completed", {
                                    hours: provider.total_hours,
                                })}
                            </span>
                            <span className="inline-flex items-center gap-1.5 leading-none">
                                <Star className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" aria-hidden />
                                {hasRating ? (
                                    <>
                                        <span className="font-semibold text-slate-700">
                                            {formatRatingAverage(provider.rating?.average ?? 0)}
                                        </span>
                                        <span>({provider.rating?.count ?? 0})</span>
                                    </>
                                ) : (
                                    intl.formatMessage("provider.card.rating_new")
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-2 text-right">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                {intl.formatMessage("profile.my_ads.applications.proposed_amount_label")}
                            </p>
                            <p className="text-2xl font-extrabold leading-tight text-gray-900">
                                {currencyFormatter.format(Number(application.amount))}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                {intl.formatMessage("profile.my_ads.applications.duration_label")}
                            </p>
                            <p className="text-sm font-bold text-gray-900">
                                {intl.formatMessage("profile.my_ads.applications.duration_hours", {
                                    duration: Number(application.duration).toString(),
                                })}
                            </p>
                        </div>
                    </div>
                </div>

                {application.message && (
                    <div className="rounded-xl bg-slate-50 px-4 py-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                            {intl.formatMessage("profile.my_ads.applications.message_label")}
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-700">{application.message}</p>
                    </div>
                )}

                <div className="border-t border-gray-100 pt-4">
                    {isPaidAccepted ? (
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap gap-2">
                                <span className={acceptedBadgeClassName}>
                                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                                    {intl.formatMessage("profile.my_ads.applications.accepted_offer")}
                                </span>
                                <span className={acceptedBadgeClassName}>
                                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                                    {intl.formatMessage("profile.my_ads.applications.payment_paid")}
                                </span>
                                {isBookingCancelled ? (
                                    <span className={cancelledBadgeClassName}>
                                        <XCircle className="h-3.5 w-3.5" aria-hidden />
                                        {intl.formatMessage("profile.my_ads.applications.cancelled")}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}
                            >
                                {status.icon}
                                {intl.formatMessage(
                                    isAwaitingPayment
                                        ? "profile.my_ads.applications.accepted_offer"
                                        : status.labelKey,
                                )}
                            </span>

                            <div className="flex flex-wrap items-center gap-2">
                                {isPending && (
                                    <Button palette="indigo" size="sm" className="rounded-xl" asChild>
                                        <Link to={offerConversationUrl}>
                                            {intl.formatMessage("profile.my_ads.applications.view_offer")}
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}

                                {isAwaitingPayment && (
                                    <Button palette="indigo" size="sm" className="rounded-xl" asChild>
                                        <Link to={offerConversationUrl}>
                                            {intl.formatMessage("profile.my_ads.applications.pay_to_finalize")}
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
};

export default ApplicationItem;
